import { useState, useMemo, useEffect } from "react"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import CompanyPipelineCard from "~/ui-rtk/components/features/pipeline-monitor/CompanyPipelineCard"
import {
  usePipelineEventControllerGetPipelineProgressQuery,
  type PipelineEventDto,
} from "~/ui-rtk/api/pipelineEventApi"

type EventDetailsModalProps = {
  event: PipelineEventDto
  onClose: () => void
}

function EventDetailsModal({ event, onClose }: EventDetailsModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl p-6 mx-4 rounded-lg bg-basic-dark-blue max-h-[80vh] overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-basic-white">
            Event Details
          </h3>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded hover:bg-basic-blue text-basic-grey-inactive"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-basic-grey-inactive">
              Event Type
            </label>
            <p className="font-mono text-sm text-basic-white break-all">
              {event.eventType}
            </p>
          </div>
          <div>
            <label className="text-sm text-basic-grey-inactive">Company</label>
            <p className="text-basic-white">{event.companyName ?? "Unknown"}</p>
          </div>
          <div>
            <label className="text-sm text-basic-grey-inactive">
              Event Time
            </label>
            <p className="text-basic-white">
              {new Date(event.eventAt).toLocaleString()}
            </p>
          </div>
          <div>
            <label className="text-sm text-basic-grey-inactive">
              Created At
            </label>
            <p className="text-basic-white">
              {new Date(event.createdAt).toLocaleString()}
            </p>
          </div>
          <div>
            <label className="text-sm text-basic-grey-inactive">Raw Data</label>
            <pre className="p-3 mt-1 overflow-auto text-xs rounded bg-basic-black text-basic-white max-h-64">
              {JSON.stringify(event.rawData, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PipelineMonitorPage() {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id
  const {
    currentData: data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = usePipelineEventControllerGetPipelineProgressQuery(
    { companyId: companyId ?? "" },
    {
      skip: !companyId,
      pollingInterval: 30000, // Refresh every 30 seconds for real-time monitoring
      refetchOnMountOrArgChange: true,
    },
  )
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)

  // A company switch can race with the session update; never render another company's response.
  const filteredCompanies = useMemo(
    () =>
      data?.companies.filter(company => company.companyId === companyId) ?? [],
    [data?.companies, companyId],
  )
  const events = useMemo(
    () => data?.events.filter(event => event.companyId === companyId) ?? [],
    [data?.events, companyId],
  )
  const selectedEvent = useMemo(
    () => events.find(event => event.id === selectedEventId),
    [selectedEventId, events],
  )

  useEffect(() => setSelectedEventId(null), [companyId])

  if (!companyId) {
    return (
      <div className="p-6 text-basic-grey-inactive">
        Select a company to view pipeline progress.
      </div>
    )
  }

  if (isLoading || (!data && isFetching)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin border-states-info" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-lg bg-states-error/10 text-states-error">
          <p className="font-semibold">Failed to load pipeline data</p>
          <p className="mt-1 text-sm">
            Make sure you have super-admin permissions to access this page.
          </p>
          <button
            onClick={() => refetch()}
            className="px-3 py-1 mt-3 text-sm rounded bg-states-error text-basic-white hover:bg-states-error/80"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-basic-white">
              Pipeline Monitor
            </h2>
            <p className="mt-1 text-basic-grey-inactive">
              Monitor pipeline progress for {currentCompany.name}. Showing
              events for today ({data?.date ?? "N/A"}).
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isFetching && !isLoading && (
              <span className="text-xs text-basic-grey-inactive">
                Refreshing...
              </span>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-3 py-1.5 text-sm rounded-lg border border-basic-blue text-basic-white hover:bg-basic-blue transition-colors disabled:opacity-50"
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Company Progress Cards */}
      <div className="mb-8">
        <h3 className="mb-4 text-lg font-medium text-basic-white">
          Company Progress
        </h3>
        {filteredCompanies.length === 0 ? (
          <div className="p-6 text-center rounded-lg bg-basic-dark-blue text-basic-grey-inactive">
            No pipeline events for the selected company today.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCompanies.map(company => (
              <CompanyPipelineCard
                key={company.companyId}
                companyName={company.companyName}
                phases={company.phases}
                progressPercent={company.progressPercent}
                overallStatus={company.overallStatus}
                totalEvents={company.totalEvents}
                lastEventAt={company.lastEventAt}
                currentStage={company.currentStage}
                failedStage={company.failedStage}
              />
            ))}
          </div>
        )}
      </div>

      {/* Raw Events Table */}
      <div>
        <h3 className="mb-4 text-lg font-medium text-basic-white">
          Raw Events ({events.length} total)
        </h3>
        <div className="overflow-hidden border rounded-lg border-basic-blue">
          <table className="w-full text-sm">
            <thead className="bg-basic-black">
              <tr>
                <th className="px-4 py-3 font-medium text-left text-basic-grey-inactive">
                  Event Type
                </th>
                <th className="px-4 py-3 font-medium text-left text-basic-grey-inactive">
                  Company
                </th>
                <th className="px-4 py-3 font-medium text-left text-basic-grey-inactive">
                  Time
                </th>
                <th className="px-4 py-3 font-medium text-left text-basic-grey-inactive">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 50).map((event, idx) => (
                <tr
                  key={event.id}
                  className={
                    idx % 2 === 0 ? "bg-basic-dark-blue" : "bg-basic-black"
                  }
                >
                  <td className="px-4 py-3 font-mono text-xs text-basic-white">
                    {event.eventType.length > 35
                      ? `${event.eventType.slice(0, 35)}...`
                      : event.eventType}
                  </td>
                  <td className="px-4 py-3 text-basic-white">
                    {event.companyName ?? "Unknown"}
                  </td>
                  <td className="px-4 py-3 text-basic-grey-inactive">
                    {new Date(event.eventAt).toLocaleTimeString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedEventId(event.id)}
                      className="px-2 py-1 text-xs transition-colors rounded bg-basic-blue text-basic-white hover:bg-basic-blue/80"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-basic-grey-inactive"
                  >
                    No events for today.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {events.length > 50 && (
            <div className="px-4 py-3 text-sm text-center bg-basic-dark-blue text-basic-grey-inactive">
              Showing first 50 of {events.length} events
            </div>
          )}
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEventId(null)}
        />
      )}
    </div>
  )
}
